import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { saveBlobAsFile } from '@/lib/download';
import { formatNumber } from '@/lib/format-es';
import { notifyErr, notifyOk } from '@/lib/notify';
import { cn } from '@/lib/utils';
import { StoreSelect, type StoreSelectOption } from '@/pages/store/components/StoreSelect';
import { storedZipBlob } from '@/utils/storedZip';
import {
    CAPTURE_DATA_MAX,
    CAPTURE_PAGE_SIZE,
    CAPTURE_PHOTO_BLOCK_SIZE,
    DEFAULT_CAPTURE_FILTERS,
    hasGeoFilter,
    toRpcFilters,
} from './captureExportModel';
import {
    buildCaptureDataZip,
    buildCapturePhotoBlockZip,
    type PreparedCaptureExport,
    type ZipFile,
} from './captureExportPack';
import { CaptureCard } from './components/CaptureCard';
import { CaptureExportPanel, type ExportBusy } from './components/CaptureExportPanel';
import { CaptureFiltersPanel } from './components/CaptureFiltersPanel';
import {
    fetchCapturePhoto,
    fetchCapturesForExport,
    useCaptureExportFacets,
    useCaptureExportList,
} from './useCaptureExport';
import type { CaptureExportFilters, CaptureExportRow, CaptureSort } from './types';

const SORT_OPTIONS: readonly StoreSelectOption[] = [
    { value: 'recent', label: 'Más recientes' },
    { value: 'weight', label: 'Más pesadas' },
    { value: 'length', label: 'Más largas' },
    { value: 'likes', label: 'Más likes' },
];
const DISTANCE_OPTION: StoreSelectOption = { value: 'distance', label: 'Más cercanas al centro' };

function saveZip(zip: ZipFile): void {
    saveBlobAsFile(storedZipBlob(zip.bytes), zip.filename);
}

function busyLabel(busy: ExportBusy): string {
    if (busy.kind === 'rows') return `Cargando ${formatNumber(busy.loaded)}/${formatNumber(busy.total)}…`;
    if (busy.kind === 'data') return 'Generando datos…';
    return `Bloque ${busy.block}: fotos ${busy.done}/${busy.total}…`;
}

export function CapturesPage() {
    const [draft, setDraft] = useState<CaptureExportFilters>(DEFAULT_CAPTURE_FILTERS);
    const [applied, setApplied] = useState<CaptureExportFilters>(DEFAULT_CAPTURE_FILTERS);
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState<Map<string, CaptureExportRow>>(() => new Map());
    const [includeAllPhotos, setIncludeAllPhotos] = useState(false);
    const [busy, setBusy] = useState<ExportBusy | null>(null);
    const [prepared, setPrepared] = useState<PreparedCaptureExport | null>(null);
    const [doneBlocks, setDoneBlocks] = useState<Set<number>>(() => new Set());

    const rpcFilters = useMemo(() => toRpcFilters(applied), [applied]);
    const facets = useCaptureExportFacets();
    const list = useCaptureExportList(rpcFilters, page, CAPTURE_PAGE_SIZE);

    const rows = list.data?.rows ?? [];
    const totalCount = list.data?.totalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / CAPTURE_PAGE_SIZE));
    const sortOptions = hasGeoFilter(applied) ? [...SORT_OPTIONS, DISTANCE_OPTION] : SORT_OPTIONS;
    const exportCount = selected.size > 0 ? selected.size : Math.min(totalCount, CAPTURE_DATA_MAX);

    const applyFilters = (next: CaptureExportFilters) => {
        const sort = next.sort === 'distance' && !hasGeoFilter(next) ? 'recent' : next.sort;
        setDraft((prev) => ({ ...prev, sort }));
        setApplied({ ...next, sort });
        setPage(1);
    };

    const toggleRow = (row: CaptureExportRow) => {
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(row.id)) next.delete(row.id);
            else next.set(row.id, row);
            return next;
        });
    };

    const selectPage = () => {
        setSelected((prev) => {
            const next = new Map(prev);
            for (const row of rows) next.set(row.id, row);
            return next;
        });
    };

    const downloadData = (snapshot: PreparedCaptureExport) => {
        setBusy({ kind: 'data' });
        try {
            saveZip(buildCaptureDataZip(snapshot));
        } catch (error) {
            notifyErr(error instanceof Error ? error.message : 'No se pudo generar el ZIP de datos');
        } finally {
            setBusy(null);
        }
    };

    const prepareExport = async () => {
        setBusy({ kind: 'rows', loaded: 0, total: exportCount });
        try {
            const manual = selected.size > 0;
            const result = manual
                ? { rows: [...selected.values()], truncated: false }
                : await fetchCapturesForExport(rpcFilters, (loaded, total) =>
                      setBusy({ kind: 'rows', loaded, total }),
                  );
            if (result.rows.length === 0) {
                notifyErr('No hay capturas que exportar con estos filtros');
                setBusy(null);
                return;
            }
            const snapshot: PreparedCaptureExport = {
                rows: result.rows,
                filters: manual ? { ...rpcFilters, seleccionManual: result.rows.length } : rpcFilters,
                includeAllPhotos,
                truncated: result.truncated,
                createdAt: new Date(),
            };
            setPrepared(snapshot);
            setDoneBlocks(new Set());
            downloadData(snapshot);
        } catch (error) {
            notifyErr(error instanceof Error ? error.message : 'No se pudieron cargar las capturas');
            setBusy(null);
        }
    };

    const downloadBlock = async (block: number) => {
        if (!prepared) return;
        setBusy({ kind: 'block', block, done: 0, total: 0 });
        try {
            const zip = await buildCapturePhotoBlockZip(prepared, block, {
                fetchPhoto: fetchCapturePhoto,
                onProgress: (done, total) => setBusy({ kind: 'block', block, done, total }),
            });
            saveZip(zip);
            setDoneBlocks((prev) => new Set(prev).add(block));
            if (zip.failures.length > 0) {
                notifyErr(
                    `Bloque ${block}: ${formatNumber(zip.photoCount)} fotos. ${zip.failures.length} no se pudieron incluir (detalle en errores-bloque-${block}.txt).`,
                );
            } else {
                notifyOk(`Bloque ${block} descargado: ${formatNumber(zip.photoCount)} fotos`);
            }
        } catch (error) {
            notifyErr(error instanceof Error ? error.message : 'No se pudo generar el ZIP de fotos');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-4">
            <PageHeader
                title="Capturas"
                description="Filtra capturas y descarga sus datos y fotos para trabajar creatividades con IA."
            >
                <Button type="button" onClick={prepareExport} disabled={busy != null || exportCount === 0}>
                    {busy?.kind === 'rows' ? (
                        <RefreshCw size={16} className="mr-2 animate-spin" />
                    ) : (
                        <Download size={16} className="mr-2" />
                    )}
                    {busy?.kind === 'rows' ? busyLabel(busy) : `Preparar exportación (${formatNumber(exportCount)})`}
                </Button>
            </PageHeader>

            {prepared && (
                <CaptureExportPanel
                    prepared={prepared}
                    busy={busy}
                    doneBlocks={doneBlocks}
                    onDownloadData={() => downloadData(prepared)}
                    onDownloadBlock={downloadBlock}
                    onDiscard={() => {
                        setPrepared(null);
                        setDoneBlocks(new Set());
                    }}
                />
            )}

            <CaptureFiltersPanel
                draft={draft}
                facets={facets.data}
                onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
                onApply={() => applyFilters(draft)}
                onReset={() => {
                    setDraft(DEFAULT_CAPTURE_FILTERS);
                    applyFilters(DEFAULT_CAPTURE_FILTERS);
                }}
            />

            <div className="flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="text-sm text-foreground">
                    <span className="font-semibold">{formatNumber(totalCount)}</span> capturas
                    {selected.size > 0 && (
                        <span className="text-primary"> · {formatNumber(selected.size)} seleccionadas</span>
                    )}
                    <p className="text-xs text-muted-foreground">
                        {selected.size > 0
                            ? 'Se exportarán solo las seleccionadas.'
                            : `Sin selección se exportan todas las del filtro. Las fotos van en bloques de ${CAPTURE_PHOTO_BLOCK_SIZE} capturas.`}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-2 text-sm text-foreground">
                        <input
                            type="checkbox"
                            className="accent-primary"
                            checked={includeAllPhotos}
                            onChange={(e) => setIncludeAllPhotos(e.target.checked)}
                        />
                        Todas las fotos de cada captura
                    </label>
                    <div className="w-48">
                        <StoreSelect
                            ariaLabel="Orden"
                            value={applied.sort}
                            options={sortOptions}
                            onChange={(value) => {
                                const sort = value as CaptureSort;
                                setDraft((prev) => ({ ...prev, sort }));
                                setApplied((prev) => ({ ...prev, sort }));
                                setPage(1);
                            }}
                        />
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={selectPage} disabled={rows.length === 0}>
                        Seleccionar página
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelected(new Map())}
                        disabled={selected.size === 0}
                    >
                        Quitar selección
                    </Button>
                </div>
            </div>

            {list.isError ? (
                <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-6 text-sm text-destructive">
                    No se pudieron cargar las capturas: {list.error instanceof Error ? list.error.message : 'error desconocido'}
                </div>
            ) : list.isLoading ? (
                <div className="py-16 text-center text-muted-foreground">
                    <RefreshCw size={20} className="mx-auto mb-2 animate-spin" />
                    Cargando capturas…
                </div>
            ) : rows.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground">
                    <Search size={24} className="mx-auto mb-2 text-muted-foreground/40" />
                    Ninguna captura cumple estos filtros
                </div>
            ) : (
                <div
                    className={cn(
                        'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6',
                        list.isFetching && 'opacity-60',
                    )}
                >
                    {rows.map((row) => (
                        <CaptureCard key={row.id} row={row} selected={selected.has(row.id)} onToggle={() => toggleRow(row)} />
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                        disabled={page <= 1}
                        aria-label="Página anterior"
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    Página {formatNumber(page)} de {formatNumber(totalPages)}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={page >= totalPages}
                        aria-label="Página siguiente"
                    >
                        <ChevronRight size={16} />
                    </Button>
                </div>
            )}
        </div>
    );
}
